"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.printReceiptRasterUploadSchema = exports.printReceiptRasterDeflatedSchema = exports.PRINT_RASTER_COMPRESSIONS = exports.printReceiptRasterSchema = exports.PRINT_RECEIPT_MAX_HEIGHT_DOTS = exports.printLogoSchema = exports.PRINT_LOGO_MAX_HEIGHT_DOTS = exports.PRINT_LOGO_BYTES_PER_ROW = exports.PRINT_LOGO_WIDTH_DOTS = void 0;
exports.printLogoByteLength = printLogoByteLength;
exports.printRasterBase64Length = printRasterBase64Length;
const zod_1 = require("zod");
/**
 * The venue's logo, as a thermal printer can actually print it.
 *
 * A receipt printer has no idea what a PNG is. It takes a bitmap: one BIT per
 * dot, black or white, packed eight dots to a byte, row by row. So the logo has
 * to be decoded, scaled, thresholded and packed before it can go anywhere near
 * a printer.
 *
 * **That conversion happens in the browser, at upload time.** The server cannot
 * decode a PNG without an image library, and this repo forbids adding
 * dependencies without asking; the staff app already has a canvas that does all
 * four steps in a dozen lines. So the operator uploads a logo as they always
 * have, the browser renders it once to printer dots, and what is stored here is
 * the result — bytes the printer takes verbatim.
 *
 * Stored ONCE, not per print: converting on every receipt would put image
 * processing on the path between a cashier pressing Settle and paper coming out.
 */
/**
 * 576 dots — the full printable width of an 80mm roll at 203 dpi, which is what
 * this venue prints on. Also the only width worth storing: a logo narrower than
 * the paper is centred by the printer, and one wider is truncated by it.
 */
exports.PRINT_LOGO_WIDTH_DOTS = 576;
/** Eight dots to the byte, so a row is this many bytes. */
exports.PRINT_LOGO_BYTES_PER_ROW = exports.PRINT_LOGO_WIDTH_DOTS / 8;
/**
 * A cap, in dots. At 203 dpi this is about 30mm of paper — generous for a logo
 * and short of the point where a receipt is mostly picture. It also bounds what
 * the upload can write to disk: 576 × 240 packed is 17 KB.
 */
exports.PRINT_LOGO_MAX_HEIGHT_DOTS = 240;
exports.printLogoSchema = zod_1.z.object({
    /** Always {@link PRINT_LOGO_WIDTH_DOTS}; carried so the payload is self-describing. */
    widthDots: zod_1.z.literal(exports.PRINT_LOGO_WIDTH_DOTS),
    heightDots: zod_1.z.number().int().min(1).max(exports.PRINT_LOGO_MAX_HEIGHT_DOTS),
    /**
     * The packed rows, base64. Exactly `heightDots × PRINT_LOGO_BYTES_PER_ROW`
     * bytes; a 1 bit is a black dot, which is the opposite of how a screen thinks
     * about it and the way every ESC/POS raster command reads it.
     */
    data: zod_1.z.string().min(1),
});
/**
 * Is this payload the right size for the height it claims?
 *
 * Shared so the browser refuses a bad conversion before uploading it and the
 * server refuses one it is handed anyway. A payload that is short by a row
 * prints as a logo with a torn edge, and one that is long shifts everything
 * after it — neither fails loudly at the printer, they just come out wrong.
 */
function printLogoByteLength(heightDots) {
    return heightDots * exports.PRINT_LOGO_BYTES_PER_ROW;
}
/* -------------------------------------------------------------------------- */
/* A WHOLE RECEIPT as dots, not just the logo.                                 */
/* -------------------------------------------------------------------------- */
/**
 * A receipt drawn as a picture, at the same width as the logo.
 *
 * WHY THIS EXISTS. The text renderer prints in the printer's own font, so a
 * "table" is `|` and `_` characters and a "rule" is a row of underscores. It is
 * honest and it works, and it can never look like the bordered, centred,
 * icon-bearing paper the owner asked it to match — those are not characters.
 * Drawing the receipt instead removes that ceiling, and removes the codepage
 * limit with it: shaped Arabic and Kurdish in a bitmap do not care what the
 * printer's factory codepage is.
 *
 * The drawing happens in the STAFF APP, on a canvas, and is packed by the same
 * browser code that already converts the logo. That is deliberate: it needs no
 * new dependency, no font file in the image and no shaping engine on the
 * server, because the browser already has all three. The arithmetic stays on
 * the server — this carries a picture of numbers the server computed, never
 * numbers the front desk worked out for itself.
 *
 * 16,384 DOTS — about 2 m of paper at 203 dpi (2026-09-28). It was 4,096
 * (~51 cm), and everything taller printed TYPED, rules and all: the owner saw
 * "on long receipts the lines between the orders become dashed, but on shorter
 * ones it's perfect", and a house tab of 421 lines could never be drawn under
 * 4,096. The staff app paints in segments of at most 4,096 rows
 * (`rasterSegments.ts`), so no canvas grows with the receipt. Receipts and
 * station tickets share this ceiling; taller than it still prints typed.
 */
exports.PRINT_RECEIPT_MAX_HEIGHT_DOTS = 16_384;
/**
 * A drawn receipt as the PRINTER takes it: packed rows, uncompressed.
 *
 * The decoded shape — what dispatch hands the ESC/POS encoder, and what the
 * printer test page carries. An upload may arrive compressed, under
 * `deflated` rather than `data`; see {@link printReceiptRasterUploadSchema}.
 */
exports.printReceiptRasterSchema = zod_1.z.object({
    widthDots: zod_1.z.literal(exports.PRINT_LOGO_WIDTH_DOTS),
    heightDots: zod_1.z.number().int().min(1).max(exports.PRINT_RECEIPT_MAX_HEIGHT_DOTS),
    /** Packed rows, base64; `heightDots × PRINT_LOGO_BYTES_PER_ROW` bytes. */
    data: zod_1.z.string().min(1),
});
/** The one compression an upload may declare: zlib-wrapped deflate (RFC 1950). */
exports.PRINT_RASTER_COMPRESSIONS = ['deflate'];
/**
 * A drawn receipt uploaded COMPRESSED: the same rows, deflated, under a key of
 * their own.
 *
 * `deflated` is the base64 of the compressed bytes, which must inflate to
 * exactly `heightDots × PRINT_LOGO_BYTES_PER_ROW`. There is deliberately NO
 * `data` here.
 *
 * WHY A SEPARATE KEY (2026-09-28). An API that knows only the uncompressed
 * shape — the build before this one, if a deploy is ever rolled back while the
 * tills and iPads keep the new staff app open — strips keys it does not know.
 * Had the compressed bytes ridden in `data` beside a `compression` flag, that
 * API would have dropped the flag, stored the compressed bytes as if they were
 * rows, claimed the job, and thrown on the byte count at every dispatch: back
 * to QUEUED, round the sweep every 30 s, and neither the drawn nor the typed
 * receipt ever printed. Without `data` that API refuses the upload as a
 * validation failure instead, the job stays QUEUED, and the typed receipt
 * prints after the grace. A row stored compressed by this build and read by
 * that one fails its parse the same way, and prints typed rather than nothing.
 */
exports.printReceiptRasterDeflatedSchema = zod_1.z.object({
    widthDots: zod_1.z.literal(exports.PRINT_LOGO_WIDTH_DOTS),
    heightDots: zod_1.z.number().int().min(1).max(exports.PRINT_RECEIPT_MAX_HEIGHT_DOTS),
    compression: zod_1.z.enum(exports.PRINT_RASTER_COMPRESSIONS),
    /** The packed rows compressed, base64. Inflates to `heightDots × 72` bytes. */
    deflated: zod_1.z.string().min(1),
});
/**
 * `POST /print-jobs/:id/raster` — a drawn receipt as the staff app SENDS it:
 * either {@link printReceiptRasterSchema} (uncompressed, `data`) or
 * {@link printReceiptRasterDeflatedSchema} (compressed, `deflated`).
 *
 * WHY COMPRESSION IS OPTIONAL (2026-09-28). A 16,384-row receipt is 1.2 MB of
 * packed rows and 1.5 MB of base64, nearly all of it white paper; the browser's
 * own `CompressionStream('deflate')` shrinks that to a few percent. So the
 * staff app compresses when it can and says so. When it cannot (a browser
 * without CompressionStream), and from every staff tab opened before this
 * shipped and not reloaded since, the upload is exactly the uncompressed shape
 * it always was — so the server takes both, and the route's body limit is
 * sized for the uncompressed worst case.
 *
 * The server stores the upload as sent and inflates it when it prints.
 */
exports.printReceiptRasterUploadSchema = zod_1.z.union([
    exports.printReceiptRasterSchema,
    exports.printReceiptRasterDeflatedSchema,
]);
/**
 * Base64 characters in an UNCOMPRESSED raster of `heightDots` rows — the
 * figure the raster route's body limit has to clear at the tallest receipt.
 */
function printRasterBase64Length(heightDots) {
    return Math.ceil(printLogoByteLength(heightDots) / 3) * 4;
}
//# sourceMappingURL=print-logo.js.map